/**
 * The two report surfaces shared by every public scan entry ramp.
 *
 * `/free-backlink-checker` and `/who-links-to-my-website` render the identical
 * numbers from the identical `Scan` snapshot; only the framing changes. So the
 * markup lives here once and each page passes its own labels, rather than the
 * two pages carrying near-identical copies that drift the first time either one
 * is restyled.
 */
import { Fragment } from 'react'
import { BrandBadge } from '#app/components/brand/badge.tsx'
import { NetworkGraph } from '#app/components/brand/network-graph.tsx'
import { type Teaser } from '#app/utils/scan-teaser.ts'

function LegendDot({ color, label }: { color: string; label: string }) {
	return (
		<span className="inline-flex items-center gap-1.5">
			<span
				className="inline-block size-2 rounded-full"
				style={{ backgroundColor: color }}
			/>
			{label}
		</span>
	)
}

/**
 * The report "front page": one atmospheric band that fuses the headline number,
 * an inline stat rail, and the network graph. No stack of equal cards.
 */
export function ReportHeader({
	teaser,
	kicker,
	countLabel,
	ariaLabel,
}: {
	teaser: Teaser
	/** Small mono eyebrow above the domain, e.g. "Backlink report". */
	kicker: string
	/** Two-line label beside the headline number, e.g. "referring\ndomains". */
	countLabel: [string, string]
	ariaLabel: string
}) {
	const metrics = [
		{ value: teaser.totalBacklinks.toLocaleString(), label: 'backlinks' },
		{
			value: teaser.highAuthority.toLocaleString(),
			label: 'high authority',
			accent: true,
		},
		{
			value: teaser.dofollowPct !== null ? `${teaser.dofollowPct}%` : 'n/a',
			label: 'dofollow',
		},
		{
			value: `+${teaser.newLast30d.toLocaleString()}`,
			label: 'new · 30d',
			accent: true,
		},
	]

	// No nodes means no canvas. `NetworkGraph` at nodeCount 0 is not an empty
	// frame: it still paints a pulsing centre orb and the domain label, and the
	// Links / Gained / Lost legend underneath describes edges that do not exist.
	// The big 0 above is the true statement, so the canvas is the part that goes,
	// and the grid collapses to one column rather than leaving a 300px hole.
	const hasNetwork = teaser.nodeCount > 0

	return (
		<section
			className="border-hairline relative mt-10 overflow-hidden rounded-[28px] border"
			aria-label={ariaLabel}
		>
			<div className="from-brand-wash via-canvas to-canvas pointer-events-none absolute inset-0 bg-gradient-to-br" />
			<div className="bg-brand/10 pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl" />
			<div
				className={`relative grid items-center gap-6 p-7 lg:p-9 ${
					hasNetwork ? 'lg:grid-cols-[0.95fr_1.05fr]' : ''
				}`}
			>
				<div>
					<div className="text-brand font-mono text-[11px] tracking-[0.18em] uppercase">
						{kicker}
					</div>
					<h2 className="font-display text-ink mt-2 text-[clamp(24px,3.4vw,38px)] leading-[1.05] font-semibold tracking-[-0.02em] break-all">
						{teaser.target}
					</h2>
					<div className="mt-6 flex items-end gap-2.5">
						<span className="font-display text-brand text-[clamp(44px,7vw,68px)] leading-none font-semibold">
							{teaser.totalReferringDomains.toLocaleString()}
						</span>
						<span className="mb-2 text-sm leading-tight text-[#57565E]">
							{countLabel[0]}
							<br />
							{countLabel[1]}
						</span>
					</div>
					<div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
						{metrics.map((m, i) => (
							<Fragment key={m.label}>
								{i > 0 ? (
									<span className="hidden h-8 w-px bg-[#E7E2F0] sm:block" />
								) : null}
								<div className="flex items-baseline gap-1.5">
									<span
										className={`font-display text-xl font-semibold ${
											m.accent ? 'text-brand' : 'text-ink'
										}`}
									>
										{m.value}
									</span>
									<span className="text-[12px] text-[#8A867D]">{m.label}</span>
								</div>
							</Fragment>
						))}
					</div>
				</div>

				{hasNetwork ? (
					<div>
						<div className="h-[240px] w-full sm:h-[300px]">
							<NetworkGraph label={teaser.target} nodeCount={teaser.nodeCount} />
						</div>
						<div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-[#8A867D]">
							<LegendDot color="#5B4BFF" label="Links" />
							<LegendDot color="#25D07A" label="Gained" />
							<LegendDot color="#F0563F" label="Lost" />
							<span className="italic opacity-70">illustrative</span>
						</div>
					</div>
				) : null}
			</div>
		</section>
	)
}

/** Clean editorial table: a header rule and row dividers, no outer box. */
export function ReferringList({
	teaser,
	heading,
	ariaLabel,
}: {
	teaser: Teaser
	heading: string
	ariaLabel: string
}) {
	// "top 0 of 0" is the same false-promise class as the gate copy, and the AR
	// footnote below is orphaned with no rows to explain. Say the true thing
	// instead and drop the furniture that only makes sense around a list.
	//
	// Keyed on the COUNT, not on `rows.length`. The two can diverge when the
	// snapshot items are missing but the provider total is not, and in that case
	// an empty `rows` would print "we found no referring domains" directly under
	// a header reading 12,000. The header's number is the public fact; the list
	// follows it.
	if (teaser.totalReferringDomains === 0) {
		return (
			<section className="mt-14" aria-label={ariaLabel}>
				<div className="border-hairline flex items-baseline justify-between border-b pb-3">
					<h3 className="font-display text-ink text-xl font-semibold">
						{heading}
					</h3>
				</div>
				<p className="mt-4 px-2 text-[15px] text-[#57565E]">
					We found no referring domains for this site.
				</p>
			</section>
		)
	}

	return (
		<section className="mt-14" aria-label={ariaLabel}>
			<div className="border-hairline flex items-baseline justify-between border-b pb-3">
				<h3 className="font-display text-ink text-xl font-semibold">
					{heading}
				</h3>
				<span className="font-mono text-xs text-[#9A968C]">
					top {teaser.rows.length} of{' '}
					{teaser.totalReferringDomains.toLocaleString()}
				</span>
			</div>
			<div>
				{teaser.rows.map((row) => (
					<div
						key={row.domain}
						className="hover:bg-brand-wash/40 grid grid-cols-[1fr_auto] items-center gap-3 border-b border-[#EFEAE6] px-2 py-3 transition-colors last:border-b-0 sm:grid-cols-[1fr_64px_96px_120px] sm:gap-4"
					>
						<span className="text-ink flex min-w-0 items-center gap-2 font-mono text-[14px]">
							<span className="truncate">{row.domain}</span>
							{row.isNew ? (
								<BrandBadge tone="new" size="sm">
									New
								</BrandBadge>
							) : null}
						</span>
						<span className="font-mono text-[14px] text-[#57565E]">
							AR {row.authority}
						</span>
						<span
							className={`hidden font-mono text-[13px] font-medium sm:block ${
								row.dofollow ? 'text-gained-ink' : 'text-[#C2410C]'
							}`}
						>
							{row.dofollow ? 'dofollow' : 'nofollow'}
						</span>
						<span className="hidden justify-self-end sm:block">
							<BrandBadge tone="stable" size="sm">
								{row.backlinks.toLocaleString()} links
							</BrandBadge>
						</span>
					</div>
				))}
			</div>
			<p className="mt-3 px-2 font-mono text-[11px] text-[#9A968C]">
				AR = Authority Rank (0 to 100). Higher is stronger.
			</p>
		</section>
	)
}
